export const DEFAULT_CONSTRAINTS = {
  audio: false,
  video: {
    facingMode: { ideal: "environment" },
    width:     { ideal: 1920 },
    height:    { ideal: 1080 },
    frameRate: { ideal: 60, min: 30 },
  }
};

export function isCameraSupported() {
  return Boolean(navigator.mediaDevices?.getUserMedia);
}

export async function startCamera({ facingMode = "environment", deviceId } = {}) {
  if (!isCameraSupported()) {
    throw new Error("Camera is not supported in this browser.");
  }
  const constraints = {
    ...DEFAULT_CONSTRAINTS,
    video: {
      ...DEFAULT_CONSTRAINTS.video,
      facingMode: deviceId ? undefined : { ideal: facingMode },
      deviceId: deviceId ? { exact: deviceId } : undefined
    }
  };
  return navigator.mediaDevices.getUserMedia(constraints);
}

export function stopStream(stream) {
  stream?.getTracks?.().forEach((track) => track.stop());
}

export function getVideoTrack(stream) {
  return stream?.getVideoTracks?.()[0] || null;
}

export function readTrackSupport(track) {
  if (!track?.getCapabilities) return {};
  const capabilities = track.getCapabilities();
  const settings = track.getSettings?.() || {};
  return {
    torch: Boolean(capabilities.torch),
    zoom: capabilities.zoom,
    focusMode: capabilities.focusMode,
    settings
  };
}

export async function setTorch(stream, enabled) {
  const track = getVideoTrack(stream);
  const support = readTrackSupport(track);
  if (!track || !support.torch) return false;
  await track.applyConstraints({ advanced: [{ torch: Boolean(enabled) }] });
  return true;
}

export async function setZoom(stream, zoom) {
  const track = getVideoTrack(stream);
  const support = readTrackSupport(track);
  if (!track || !support.zoom) return false;
  const value = Math.min(support.zoom.max, Math.max(support.zoom.min, Number(zoom)));
  await track.applyConstraints({ advanced: [{ zoom: value }] });
  return value;
}

export async function tapToFocus(stream, point) {
  const track = getVideoTrack(stream);
  if (!track?.applyConstraints) return false;
  try {
    await track.applyConstraints({
      advanced: [{ focusMode: "continuous", pointsOfInterest: [point] }]
    });
    return true;
  } catch {
    return false;
  }
}

export async function captureFrame(video, { mirror = false, type = "image/jpeg", quality = 0.95 } = {}) {
  if (!video?.videoWidth || !video?.videoHeight) throw new Error("Camera preview is not ready yet.");
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d", { alpha: false });
  if (mirror) {
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Unable to capture photo."));
    }, type, quality);
  });
}

// ── Auto Photo Enhancement ────────────────────────────────────────────────────
// Pipeline: auto-levels → grey-world WB → gamma/shadow-lift → vibrance → sharpen
// All pixel ops run on ImageData (no external libs, single blob in / blob out).

// Natural Polish — invisible improvements: shadow lift + gentle vibrance + light sharpen.
// No auto-levels, no white balance shift — colours stay true to the scene.
export function autoEnhanceBlob(blob) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(url);
      const W = img.naturalWidth, H = img.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d", { alpha: false, willReadFrequently: true });
      ctx.drawImage(img, 0, 0);

      const imgData = ctx.getImageData(0, 0, W, H);
      const d = imgData.data;
      const len = d.length;

      // ── 1. Build a single LUT: gentle gamma (shadow lift only) ───────────
      // gamma 0.96 = barely noticeable shadow lift, zero highlight change
      const GAMMA = 0.96;
      const lut = new Uint8Array(256);
      for (let v = 0; v < 256; v++) {
        lut[v] = Math.round(Math.pow(v / 255, GAMMA) * 255);
      }

      // ── 2. Apply gamma + very subtle vibrance in one pass ────────────────
      const VIB = 0.10;   // very gentle — just stops greys from looking flat
      for (let i = 0; i < len; i += 4) {
        let r = lut[d[i]], g = lut[d[i+1]], b = lut[d[i+2]];

        // Vibrance: only boost the least-saturated pixels, leave vivid ones alone
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        if (mx > mn) {
          const sat   = (mx - mn) / mx;
          const boost = (1 - sat) * (1 - sat) * VIB;   // squared = even gentler on mid-sat
          const luma  = 0.299 * r + 0.587 * g + 0.114 * b;
          r = Math.min(255, Math.max(0, r + (r - luma) * boost)) | 0;
          g = Math.min(255, Math.max(0, g + (g - luma) * boost)) | 0;
          b = Math.min(255, Math.max(0, b + (b - luma) * boost)) | 0;
        }
        d[i] = r; d[i+1] = g; d[i+2] = b;
      }

      // ── 3. Light sharpening (5-tap laplacian, very conservative) ─────────
      const orig  = new Uint8ClampedArray(d);
      const W4    = W * 4;
      const SHARP = 0.20;   // subtle — adds crispness without halos
      for (let y = 1; y < H - 1; y++) {
        for (let x = 1; x < W - 1; x++) {
          const k = (y * W + x) * 4;
          for (let c = 0; c < 3; c++) {
            const ctr = orig[k + c];
            const lap = 4 * ctr - orig[k - W4 + c] - orig[k + W4 + c]
                                 - orig[k - 4  + c] - orig[k + 4  + c];
            d[k + c]  = Math.min(255, Math.max(0, (ctr + lap * SHARP) | 0));
          }
        }
      }

      ctx.putImageData(imgData, 0, 0);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Polish export failed"))),
        "image/jpeg", 0.95
      );
    };

    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Polish load failed")); };
    img.src = url;
  });
}

export function vibrate(pattern = 55) {
  // navigator.vibrate: supported on Android Chrome/WebView, not on iOS Safari.
  if (navigator.vibrate) {
    navigator.vibrate(pattern);
    return;
  }
  // iOS fallback: a very short AudioContext click gives tactile-like audio feedback
  // since the Vibration API is not available on iOS at all.
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.01, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.06;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start();
    src.onended = () => ctx.close();
  } catch { /* silent */ }
}
